import React from 'react';
import styled from 'styled-components';
import { getIcon } from '../../../utils';
import { AnchorText, Col, Container, Grid, Section } from '../../common';
import { NavbarPrimaryLogo } from './NavbarPrimaryLogo';

const StyledIcon = styled.span`
  svg {
    color: ${props => props.$color};
    &:hover {
      color: ${props => props.$colorHover};
    }
  }
`;

const FooterLinks = ({ page, sanity }) => {
  const { business, colors, pages, footer, services } = sanity;
  const { gbp } = page;
  const visible = footer?.visibleColumns || [];

  const physicalAddress = [gbp?.address, gbp?.city, gbp?.stateShort, gbp?.zip]
    .filter(Boolean)
    .join(', ');

  const contact = [
    { id: 1, icon: 'Phone', anchor: gbp?.phone, url: `tel:${gbp?.phone}` },
    { id: 2, icon: 'Email', anchor: gbp?.email, url: `mailto:${gbp?.email}` },
    { id: 3, icon: 'Address', anchor: physicalAddress, url: gbp?.gbpUrl },
    { id: 4, icon: 'Hours', anchor: gbp?.hoursWeek, url: gbp?.gbpUrl },
  ];

  const socials = [
    { id: 1, icon: 'Facebook', url: business?.facebookUrl },
    { id: 2, icon: 'Instagram', url: business?.instagramUrl },
    { id: 3, icon: 'Twitter', url: business?.twitterUrl },
    { id: 4, icon: 'LinkedIn', url: business?.linkedInUrl },
    { id: 5, icon: 'YouTube', url: business?.youTubeUrl },
    { id: 6, icon: 'TikTok', url: business?.tikTokUrl },
    { id: 7, icon: 'Yelp', url: business?.yelpUrl },
  ];

  const renderServicePages = () => (
    <ul className="mt-4 space-y-4">
      {pages
        ?.sort((a, b) => a?.order - b?.order)
        ?.filter(p => p?.type === 'service' && p?.addToFooter)
        .map(p => (
          <li key={p?.slug?.current}>
            <AnchorText
              sanity={sanity}
              type="internal"
              color={colors?.neutral?.default}
              colorHover={colors?.accent?.default}
              to={p?.slug?.current}
              className="text-base break-words"
            >
              <span className="text-sm break-words">{p?.name}</span>
            </AnchorText>
          </li>
        ))}
    </ul>
  );

  const renderServiceList = () => (
    <ul className="mt-4 space-y-4">
      {services
        ?.sort((a, b) => a?.order - b?.order)
        .map(service => (
          <li key={service?.slug?.current}>
            <AnchorText
              sanity={sanity}
              type="internal"
              color={colors?.neutral?.default}
              colorHover={colors?.accent?.default}
              to={service?.slug?.current}
              className="text-base break-words"
            >
              <span className="text-sm break-words">{service?.name}</span>
            </AnchorText>
          </li>
        ))}
    </ul>
  );

  const colCount = visible.length;
  const gridColsClass =
    colCount === 1
      ? 'grid-cols-1'
      : colCount === 2
        ? 'grid-cols-1 lg:grid-cols-[3fr_2fr]'
        : colCount === 3
          ? 'grid-cols-1 lg:grid-cols-[2fr_1fr_1fr]'
          : 'grid-cols-1 lg:grid-cols-[2fr_1fr_1fr_1fr]';

  return (
    <Section idName="footer-links" padding="sm" bgColor={colors?.neutral?.lighter}>
      <Container padding="sm" classes="py-12 lg:py-16">
        <Grid classes={`${gridColsClass} gap-12`}>
          {colCount === 2 ? (
            <Col>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-4 items-start">
                <div className="flex flex-col items-center space-y-6 lg:items-start">
                  <NavbarPrimaryLogo sanity={sanity} />
                  <ul className="flex justify-center space-x-4 lg:justify-start">
                    {socials.map(
                      social =>
                        social?.url && (
                          <li key={social?.icon}>
                            <AnchorText
                              sanity={sanity}
                              type="external"
                              color={colors?.neutral?.light}
                              colorHover={colors?.accent?.light}
                              href={social?.url}
                            >
                              <span className="sr-only">{social?.icon}</span>
                              <StyledIcon
                                $color={colors?.accent?.default}
                                $colorHover={colors?.accent?.dark}
                                className="!text-3xl"
                              >
                                {React.createElement(getIcon(social?.icon))}
                              </StyledIcon>
                            </AnchorText>
                          </li>
                        )
                    )}
                  </ul>
                </div>
                <div className="flex justify-center text-center lg:justify-start lg:text-left">
                  {gbp?.slogan && <p className="text-base italic text-gray-500">{gbp?.slogan}</p>}
                </div>
              </div>
            </Col>
          ) : (
            <Col classes="flex flex-col space-y-6 justify-center text-center lg:justify-start">
              <NavbarPrimaryLogo sanity={sanity} />
              {gbp?.slogan && (
                <p className="max-w-xs mx-auto text-base italic text-gray-500">{gbp?.slogan}</p>
              )}
              <ul className="flex justify-center mt-12 space-x-6">
                {socials.map(
                  social =>
                    social?.url && (
                      <li key={social?.icon}>
                        <AnchorText
                          sanity={sanity}
                          type="external"
                          color={colors?.neutral?.light}
                          colorHover={colors?.accent?.light}
                          href={social?.url}
                        >
                          <span className="sr-only">{social?.icon}</span>
                          <StyledIcon
                            $color={colors?.accent?.default}
                            $colorHover={colors?.accent?.dark}
                            className="!text-3xl"
                          >
                            {React.createElement(getIcon(social?.icon))}
                          </StyledIcon>
                        </AnchorText>
                      </li>
                    )
                )}
              </ul>
            </Col>
          )}

          {visible.includes('company') && (
            <Col classes="text-center lg:text-left">
              <h4 className="font-semibold tracking-tight uppercase text-md text-zinc-800">
                Our Company
              </h4>
              <ul className="mt-4 space-y-4">
                {pages
                  ?.sort((a, b) => a?.order - b?.order)
                  ?.filter(p => p?.type === 'brand' && p?.addToFooter)
                  .map(p => (
                    <li key={p?.slug?.current}>
                      <AnchorText
                        sanity={sanity}
                        type="internal"
                        color={colors?.neutral?.default}
                        colorHover={colors?.accent?.default}
                        to={p?.slug?.current}
                        className="text-base break-words"
                      >
                        <span className="text-sm break-words">{p?.name}</span>
                      </AnchorText>
                    </li>
                  ))}
              </ul>
            </Col>
          )}

          {visible.includes('services') && (
            <Col classes="text-center lg:text-left">
              <h4 className="font-semibold tracking-tight uppercase text-md text-zinc-800">
                Our Services
              </h4>
              {footer?.serviceListSource === 'serviceList'
                ? renderServiceList()
                : renderServicePages()}
            </Col>
          )}

          {visible.includes('contact') && (
            <Col classes="text-center lg:text-left">
              <h4 className="font-semibold tracking-tight uppercase text-md text-zinc-800">
                Get In Touch
              </h4>
              <ul className="mt-4">
                {contact?.map(item => (
                  <li key={item?.id} className="pt-4 first:pt-0">
                    <AnchorText
                      sanity={sanity}
                      type="external"
                      color={colors?.neutral?.default}
                      colorHover={colors?.accent?.default}
                      href={item?.url}
                      className="text-base break-words"
                    >
                      <span className="text-sm break-words">{item?.anchor}</span>
                    </AnchorText>
                  </li>
                ))}
              </ul>
            </Col>
          )}
        </Grid>
      </Container>
    </Section>
  );
};

export { FooterLinks };
